import { defineAsyncComponent } from 'vue'

import type { ReportFilter } from '../models/filter.types'
import type {
  ReportLayoutConfig,
  ReportSectionTabConfig,
  SectionBuild,
} from '../models/layoutConfig.types'

// v3 `dashboardConfig` key-events trendline → module 09 `DashboardChartArea`. Lazy, not a static
// barrel import: `OverviewChartWidget` (inside `DashboardChartArea`) itself imports `@/features/
// reports` — this file is part of `reports/configs/**`, which every other file in this directory
// keeps free of cross-feature imports (08 is the foundation 09 is built on, never the reverse).
// A static import here would close that into a real cycle; the dynamic import keeps it a
// separate chunk boundary instead.
const DashboardChartArea = defineAsyncComponent(() =>
  import('@/features/dashboards').then((module) => module.DashboardChartArea),
)

// v3 `configs/BudgetAllocation/BudgetAllocation.js` (302 ln). Port notes:
// - v3 column shaping waits for ORP-18643: breakdowns' `showCols: ['marketing_channel']` on
//   Overall plus the Meta/Tiktok `hideCols`/`addCols` reach+frequency shuffle. Key events'
//   `showKeyEvents: true` and its 15-column `hideCols` strip have landed (see `keyEventsBuild`).
// - `configBudget.visibleType: 'double'` + `showTableSlot` → chart-table composition;
//   `keyEventConfig.hideChartButton` stays table-only. `fieldValues` role hints and return-level
//   `channel` logo hints drop (v4 derives chart field roles; the rail owns logos).
// - `chartSortMetric: 'cost'` was redundant with `sortField: 'cost'` and drops.

export interface BudgetAllocationDetail {
  /** Machine identity — rail selection + platform logo key, never displayed raw. */
  name: string
  /** Campaign-level schedule tabs (also the availability gate on every page tab). */
  tabs: string[]
  adGroupTabs: string[]
  adTabs: string[]
  channel?: string
  checkDimension?: { field: string; value: string[] }
}

// v3 `BUDGET_ALLOCATION_DETAILS` (:3-180) verbatim — also consumed by the v3 Summary
// acquisition chart and CreativeDashboard, so it stays exported for those ports.
// v3 `Overall.defaultFilters: []` was dead (no v3 config fn read tab defaultFilters) and drops.
export const BUDGET_ALLOCATION_DETAILS: BudgetAllocationDetail[] = [
  { name: 'Overall', tabs: ['overview'], adGroupTabs: ['overview'], adTabs: ['overview'] },
  {
    name: 'Google',
    tabs: ['google_ads'],
    adGroupTabs: ['google_ads_adgroup'],
    adTabs: ['google_ads_ad'],
    channel: 'google_ads',
  },
  {
    name: 'Meta',
    tabs: ['meta_ads'],
    adGroupTabs: ['meta_ads_adgroup'],
    adTabs: ['meta_ads_ad'],
    channel: 'meta_ads',
  },
  {
    name: 'Criteo',
    tabs: ['criteo'],
    adGroupTabs: ['criteo_adgroup'],
    adTabs: ['criteo_ad'],
    channel: 'criteo',
  },
  {
    name: 'Tiktok',
    tabs: ['tiktok_ads'],
    adGroupTabs: ['tiktok_ads_adgroup'],
    adTabs: ['tiktok_ads_ad'],
    channel: 'tiktok_ads',
  },
  {
    name: 'Linkedin',
    tabs: ['linkedin'],
    adGroupTabs: ['linkedin_adgroup'],
    adTabs: ['linkedin_ad'],
    channel: 'linkedin',
  },
  {
    name: 'Snapchat',
    tabs: ['snapchat_ads'],
    adGroupTabs: ['snapchat_ads_adgroup'],
    adTabs: ['snapchat_ads_ad'],
    channel: 'snapchat_ads',
  },
  {
    name: 'RTB',
    tabs: ['rtb_house'],
    adGroupTabs: ['rtb_house_adgroup'],
    adTabs: ['rtb_house_ad'],
    channel: 'rtb_house',
  },
  {
    name: 'DV360',
    tabs: ['dv360'],
    adGroupTabs: ['dv360_adgroup'],
    adTabs: ['dv360_ad'],
    channel: 'dv360',
  },
  {
    name: 'Cimri',
    tabs: ['cimri'],
    adGroupTabs: ['cimri_adgroup'],
    adTabs: ['cimri_ad'],
    channel: 'cimri',
  },
  {
    name: 'Reklamstore',
    tabs: ['reklamstore'],
    adGroupTabs: ['reklamstore_adgroup'],
    adTabs: ['reklamstore_ad'],
    channel: 'reklamstore',
  },
  {
    name: 'Akakce',
    tabs: ['akakce'],
    adGroupTabs: ['akakce_adgroup'],
    adTabs: ['akakce_ad'],
    channel: 'akakce',
  },
  {
    name: 'Gelirortaklari',
    tabs: ['gelirortaklari'],
    adGroupTabs: ['gelirortaklari_adgroup'],
    adTabs: ['gelirortaklari_ad'],
    channel: 'gelirortaklari',
  },
  {
    name: 'Yandex',
    tabs: ['yandex'],
    adGroupTabs: ['yandex_adgroup'],
    adTabs: ['yandex_ad'],
    channel: 'yandex',
  },
  {
    name: 'Apple Search Ads',
    tabs: ['apple_search_ads'],
    adGroupTabs: ['apple_search_ads_adgroup'],
    adTabs: ['apple_search_ads_ad'],
    channel: 'apple_search_ads',
  },
  {
    name: 'App Samurai',
    tabs: ['app_samurai'],
    adGroupTabs: ['app_samurai_adgroup'],
    adTabs: ['app_samurai_ad'],
    channel: 'app_samurai',
  },
  {
    name: 'Bing Ads',
    tabs: ['bing_ads'],
    adGroupTabs: ['bing_ads_adgroup'],
    adTabs: ['bing_ads_ad'],
    channel: 'bing_ads',
  },
  {
    name: 'Yango',
    tabs: ['yango'],
    adGroupTabs: ['yango_adgroup'],
    adTabs: ['yango_ad'],
    channel: 'yango',
  },
  {
    name: 'Hopi',
    tabs: ['hopi'],
    adGroupTabs: ['hopi_adgroup'],
    adTabs: ['hopi_ad'],
    channel: 'hopi',
  },
  {
    name: 'Twitter',
    tabs: ['twitter_ads'],
    adGroupTabs: ['twitter_ads_adgroup'],
    adTabs: ['twitter_ads_ad'],
    channel: 'twitter_ads',
  },
  // v3 carries two 'Yandex' rows ('yandex' above, 'yandex_ads' here) — kept verbatim; they are
  // distinct machine rows and at most one resolves against a given workspace's schedules.
  {
    name: 'Yandex',
    tabs: ['yandex_ads'],
    adGroupTabs: ['yandex_ads_adgroup'],
    adTabs: ['yandex_ads_ad'],
    channel: 'yandex_ads',
  },
  {
    name: 'Pinterest',
    tabs: ['pinterest_ads'],
    adGroupTabs: ['pinterest_ads_adgroup'],
    adTabs: ['pinterest_ads_ad'],
    channel: 'pinterest_ads',
  },
  {
    name: 'Reddit',
    tabs: ['reddit_ads'],
    adGroupTabs: ['reddit_ads_adgroup'],
    adTabs: ['reddit_ads_ad'],
    channel: 'reddit_ads',
  },
  {
    name: 'CM360',
    tabs: ['cm360'],
    adGroupTabs: ['cm360_adgroup'],
    adTabs: ['cm360_ad'],
    channel: 'cm360',
  },
  {
    name: 'Trivago',
    tabs: ['trivago'],
    adGroupTabs: ['trivago_adgroup'],
    adTabs: ['trivago_ad'],
    channel: 'trivago',
  },
  {
    name: 'Sheet',
    tabs: ['metrics_sheet_campaign'],
    checkDimension: { field: 'marketing_channel', value: ['sheet_custom_channel'] },
    adGroupTabs: ['metrics_sheet_campaign'],
    adTabs: ['metrics_sheet_campaign'],
    channel: 'sheet_custom_channel',
  },
]

const channelFilters = (channel: string | undefined): ReportFilter[] =>
  channel ? [{ field: 'marketing_channel', match_mode: 'equals', value: channel }] : []

const detailTabs = (
  entityType: string,
  pickTabs: (detail: BudgetAllocationDetail) => string[],
): ReportSectionTabConfig[] =>
  BUDGET_ALLOCATION_DETAILS.map((detail) => ({
    name: detail.name,
    tabs: pickTabs(detail),
    entityType,
    ...(detail.channel ? { channel: detail.channel } : {}),
    ...(detail.checkDimension ? { checkDimension: detail.checkDimension } : {}),
  }))

// v3 Key Events section consumed `BUDGET_ALLOCATION_DETAILS` raw (no entity_type).
const KEY_EVENT_TABS: ReportSectionTabConfig[] = BUDGET_ALLOCATION_DETAILS.map((detail) => ({
  name: detail.name,
  tabs: detail.tabs,
  ...(detail.channel ? { channel: detail.channel } : {}),
  ...(detail.checkDimension ? { checkDimension: detail.checkDimension } : {}),
}))

// v3 `configBudget(tab, type, id, dimension)` — the request always targets the 'overview'
// schedule tab; the rail tab's own `tabs` only gate availability.
const configBudgetBuild =
  (type: string, reportId: number, dimension: string): SectionBuild =>
  ({ tab }) => ({
    id: `${reportId}-${type}-breakdown-${tab.name}`,
    reportId,
    scheduleTabs: ['overview'],
    view: 'table',
    composition: 'chart-table',
    chartType: 'compare_column',
    chartDimensions: [dimension],
    chartMetrics: ['cost'],
    addCompare: true,
    sortField: 'cost',
    filters: channelFilters(tab.channel),
  })

/**
 * v3 `keyEventConfig` `hideCols` (:224-240) — the standard performance metric set, stripped so the
 * table shows key-event columns alone. They stay offered in the customize panel; only the default
 * selection drops them.
 */
const KEY_EVENT_HIDDEN_FIELDS = [
  'impressions',
  'ctr',
  'clicks',
  'cpc',
  'conversions',
  'conversions_value',
  'roas',
  'cost_per_conversion',
  'mmp_sessions',
  'mmp_purchase_events',
  'mmp_revenue',
  'mp_sessions',
  'mp_revenue',
  'cr',
  'mp_conversions',
]

// v3 `keyEventConfig`. `showCols: ['marketing_channel']` on Overall still waits for ORP-18643.
const keyEventsBuild: SectionBuild = ({ tab }) => ({
  id: `6-key-events-${tab.name}`,
  reportId: 6,
  scheduleTabs: tab.channel ? [tab.channel] : (tab.tabs ?? []),
  // v3 `hideChartButton: true` → table-only.
  view: 'table',
  allowedViews: ['table'],
  showKeyEvents: true,
  hideFields: KEY_EVENT_HIDDEN_FIELDS,
  filters: channelFilters(tab.channel),
})

export const BUDGET_ALLOCATION: ReportLayoutConfig = [
  {
    name: 'Campaign',
    labelKey: 'reports.budgetAllocation.tabs.campaign',
    sections: [
      {
        id: '6-campaign-breakdown',
        reportId: 6,
        compareDefault: true,
        build: configBudgetBuild('campaign', 6, 'campaign_name'),
        tabs: detailTabs('campaign', (detail) => detail.tabs),
      },
    ],
  },
  {
    name: 'Ad Group',
    labelKey: 'reports.budgetAllocation.tabs.adGroup',
    sections: [
      {
        id: '68-ad-group-breakdown',
        reportId: 68,
        compareDefault: true,
        build: configBudgetBuild('ad-group', 68, 'adgroup_name'),
        tabs: detailTabs('adgroup', (detail) => detail.adGroupTabs),
      },
    ],
  },
  {
    name: 'Ad',
    labelKey: 'reports.budgetAllocation.tabs.ad',
    sections: [
      {
        id: '66-ad-breakdown',
        reportId: 66,
        compareDefault: true,
        build: configBudgetBuild('ad', 66, 'ad_name'),
        tabs: detailTabs('ad', (detail) => detail.adTabs),
      },
    ],
  },
  {
    name: 'Key Events',
    labelKey: 'reports.budgetAllocation.tabs.keyEvents',
    requiresKeyEventMapping: true,
    sections: [
      {
        id: '6-key-events-dash',
        reportId: 6,
        compareDefault: true,
        renderer: DashboardChartArea,
        rendererMode: 'prepend',
        rendererProps: {
          // v3 `keyEventDashboardConfig.keyEvents: true` — the trendline plots key events only, so
          // the v3 `selectedMetrics` default (cost/cpc/purchase_value/roas) never matches and the
          // chart falls back to the first four key-event metrics, exactly as v3 did.
          keyEvents: true,
          defaultMetricFields: ['cost', 'cpc', 'purchase_value', 'roas'],
        },
        build: keyEventsBuild,
        tabs: KEY_EVENT_TABS,
      },
    ],
  },
]
